import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { CustomHamburgerMenuService } from '@main/categories-menu/feature';
import { Step } from '@main/mobile-menu/const';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { MobileMenuActions } from '../store/actions';
import { MainSelectors } from '../store/selectors';

@Injectable({ providedIn: 'root' })
export class MobileMenuFacade implements OnDestroy {
  subscription = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    private hamburgerMenuService: CustomHamburgerMenuService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.subscription.add(
      this.router.events
        .pipe(
          filter<NavigationStart>((event: NavigationStart) => event instanceof NavigationStart),
          filter<NavigationStart>(
            (event) => event.navigationTrigger === 'popstate' || event.navigationTrigger === 'imperative',
          ),
          filter<NavigationStart>(() => this.hamburgerMenuService.isExpanded.value),
        )
        .subscribe((event) => {
          const search = '?' + event.url.split('?')[1];
          const urlSearchParams = new URLSearchParams(search);
          const step = +urlSearchParams.get('step');

          this.getDepth()
            .pipe(take(1))
            .subscribe((depth) => {
              if (!step) {
                this.hamburgerMenuService.toggle(true);
              } else {
                if (step > depth) {
                  this.simpleForward();
                } else if (step < depth) {
                  this.store.dispatch(new MobileMenuActions.MobileMenuPrevStep());
                }
              }
            });
        }),
    );

    this.initialize();
  }

  initialize(): void {
    this.store.dispatch(new MobileMenuActions.MobileMenuResetSteps());
  }

  simpleForward(): void {
    this.store.dispatch(new MobileMenuActions.MobileMenuSimpleForward());
  }

  nextStep(step: Step): void {
    this.getDepth()
      .pipe(take(1))
      .subscribe((depth: number) => {
        this.router.navigate([], {
          queryParams: { step: depth + 1 },
          queryParamsHandling: 'merge',
          relativeTo: this.activatedRoute,
        });
      });

    this.store.dispatch(new MobileMenuActions.MobileMenuNextStep(step));
  }

  openMobileMenu(step: Step): void {
    this.getDepth()
      .pipe(take(1))
      .subscribe((depth) => {
        if (depth > 1) {
          history.go(-(depth - 1));
        }

        this.store.dispatch(
          new MobileMenuActions.MobileMenuSetState({
            steps: [step],
            depth: 1,
          }),
        );
      });
  }

  prevStep(): void {
    history.back();
  }

  storeReset(): void {
    this.store.dispatch(new MobileMenuActions.MobileMenuResetSteps());
  }

  resetSteps(): void {
    this.getDepth()
      .pipe(take(1))
      .subscribe((step) => {
        history.go(-step);
      });

    this.storeReset();
  }

  getCurrentStep(): Observable<Step | null> {
    return this.store.pipe(select(MainSelectors.getMobileMenuCurrentStep));
  }

  getDepth(): Observable<number | null> {
    return this.store.pipe(select(MainSelectors.getMobileMenuDepth));
  }

  getPrevStep(): Observable<Step | null> {
    return this.store.pipe(select(MainSelectors.getMobileMenuPrevStep));
  }

  isRootMatch(rootNodeId: string): Observable<boolean> {
    return this.store.pipe(
      select(MainSelectors.getMobileMenuHistory),
      map((steps) => {
        if (!steps?.length) {
          return false;
        }

        return steps[0].componentName === rootNodeId;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
